<script>
  import { mapActions, mapGetters } from 'vuex';

  import SignupDialog from '@/components/SignupDialog.vue';
  import LoginDialog from '@/components/LoginDialog.vue';
  import ResetPasswordDialog from '@/components/ResetPasswordDialog.vue';
  import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
  import AlertDialog from '@/components/dialogs/AlertDialog.vue';
  import MessageDialog from '@/components/dialogs/MessageDialog.vue';
  import DialogGame from '@/components/dialogs/DialogFrame.vue';
  import DialogAddFriends from '@/components/dialogs/DialogFrame2.vue';
  import PromptDialog from '@/components/dialogs/PromptDialog.vue';
  import AlertSnackBar from '@/components/popups/AlertSnackBar.vue';
  import EventDay from '@/components/dialogs/EventDay.vue';
  import VideoPopup from '@/components/dialogs/VideoPopup.vue';
  import PromoPackPopup from '@/components/dialogs/PromoPackPopup.vue';
  import CashierPopup from '@/components/dialogs/CashierPopup.vue';
  import RoomBuyInPopup from '@/components/dialogs/RoomBuyInPopup.vue';
  import breakpoints from '@/mixins/breakpoints';
  import { footerPages, halloweenPaths, landingPages } from '@/router';
  import { Navigation } from '@/mixins/navigation';
  import utmUtils from '@/helpers/utm-utils';

  function delay(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  export default {
    mixins: [breakpoints, Navigation],

    components: {
      AlertSnackBar,
      DialogGame,
      EventDay,
      DialogAddFriends,
      BaseView: () => import('./View'),
      SideBar: () => import('./Sidebar'),
      AppBar: () => import('./AppBar'),
      MessageDialog,
      AlertDialog,
      ConfirmDialog,
      LoginDialog,
      SignupDialog,
      ResetPasswordDialog,
      PromptDialog,
      PromoPackPopup,
      CashierPopup,
      VideoPopup,
      RoomBuyInPopup,
    },

    data: () => ({
      appBarMounted: false,
      logoStyle: {},
    }),

    computed: {
      ...mapGetters(['getThemeMode']),

      isSidebarOpened() {
        const state = this.$store.state.sidebar;
        return this.isMobileOrTablet ? state : !state;
      },

      showSidebarOverlay() {
        const width = this.$vuetify.breakpoint.width;
        if (width < 960 || width > 1280) return false;
        if (!this.isLoggedInUser) return false;
        return this.isSidebarOpened;
      },

      landingPageRoutes() {
        const routes = ['/july4th', '/tmb', '/discord', ...halloweenPaths, ...landingPages];

        if (!this.$store.state.user) {
          routes.push('/');
        }

        return routes;
      },
      isRouteIncluded() {
        return this.$store.getters.checkStringInArray(this.$router.history.current.name); // Pass the string to check here
      },
      isLandingPage() {
        return this.landingPageRoutes.includes(this.$route.path);
      },
      isFooterLoggedOutPage() {
        return footerPages.includes(this.$route.path) && !this.isLoggedInUser;
      },
      showLoginDialog() {
        return this.$store.getters['showLoginDialog'];
      },
      showSignupDialog() {
        return this.$store.getters.showSignupDialog;
      },
      showResetPasswordDialog() {
        return this.$store.getters['showResetPasswordDialog'];
      },

      theme() {
        return this.$vuetify.theme.dark ? 'dark' : 'light';
      },

      isLoggedInUser() {
        const userExists = !!this.$store.getters.getuser;
        return userExists && !this.isSocialSignUp;
      },

      isSocialSignUp() {
        return this.$store.state.isSocialSignUp;
      },
    },

    watch: {
      isSidebarOpened: {
        handler() {
          this.calcLogoStyle();
        },
        immediate: true,
      },
      appBarMounted() {
        this.calcLogoStyle();
      },
      isLoggedInUser() {
        this.calcLogoStyle();
      },
    },

    created() {
      if (!this.isLoggedInUser) this.handleUTMParams();

      let timeout = null;

      window.addEventListener('offline', () => {
        clearTimeout(timeout);

        timeout = setTimeout(() => {
          this.$store.dispatch('showAlertDialog', {
            title: 'No internet connection',
            text: 'Connection lost. Trying to connect…',
            permanent: true,
          });
        }, 5000);
      });

      window.addEventListener('online', () => {
        clearTimeout(timeout);
      });

      window.addEventListener('resize', this.calcLogoStyle);
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.calcLogoStyle);
    },

    methods: {
      ...mapActions(['toggleSidebar']),

      handleUTMParams() {
        utmUtils.handleUTMParams();
        this.$store.dispatch('callTrafficTracker', utmUtils.getUTMParams());
      },

      goToLobby() {
        const url = this.$store.state.user ? '/lobby' : '/';
        this.navigateTo(url);
      },

      async calcLogoStyle() {
        const screenWidth = this.$vuetify.breakpoint.width;
        const hasMobileLayout = screenWidth <= 960;
        const logoHeight = hasMobileLayout ? 24 : 30;

        const appBarHeight = 82;
        const topOffset = (appBarHeight - logoHeight) / 2;

        let translateString = `translateY(${topOffset}px)`;
        let display = 'none';

        const calcOffsetFromMenu = (translateString) => {
          const menuToggleEl = document.getElementById('menu-toggle');
          if (menuToggleEl) {
            const bounds = menuToggleEl.getBoundingClientRect();
            const paddingLeft = 12;
            const offsetX = bounds.x + bounds.width + paddingLeft;
            translateString += ` translateX(${offsetX}px)`;
          }
          return translateString;
        };

        const calcOffsetFromTopNav = (translateString) => {
          const topNavEl = document.getElementById('top-nav');
          if (topNavEl) {
            const bounds = topNavEl.getBoundingClientRect();
            translateString += ` translateX(${bounds.x}px)`;
          }
          return translateString;
        };

        if (this.appBarMounted) {
          await delay(150);

          const pageWithoutSidebar =
            !this.isLoggedInUser && (footerPages.includes(this.$route.path) || this.$route.path === '/');

          if (hasMobileLayout) {
            if (this.isLoggedInUser) {
              translateString = calcOffsetFromMenu(translateString);
            } else {
              translateString = calcOffsetFromTopNav(translateString);
            }
          } else {
            if (pageWithoutSidebar) {
              translateString = calcOffsetFromTopNav(translateString);
            } else if (this.isSidebarOpened) {
              translateString = calcOffsetFromMenu(translateString);
            } else {
              translateString = calcOffsetFromTopNav(translateString);
            }
          }

          display = 'block';
        }

        this.logoStyle = { transform: translateString, display };
      },

      hideSidebar() {
        this.$store.commit('toggleVerticalSidebarDrawer');
        this.toggleSidebar(true);
      },
    },
  };
</script>

<template>
  <!-- index.vue -->

  <v-app
    v-reload-on-fail
    :class="{
      'app-admin-wrap-layout-1 sidebar-mini': getThemeMode.verticalSidebarMini,
      'sidebar-close': !getThemeMode.verticalSidebarDrawer,
    }"
    :style="{
      background: $vuetify.theme.themes[theme].background,
      color: $vuetify.theme.themes[theme].color,
    }"
  >
    <div class="sidebar-overlay" :class="{ 'sidebar-overlay--open': showSidebarOverlay }" @click="hideSidebar" />

    <div
      ref="mqLogo"
      :class="{ 'logo-container--logged-in': isLoggedInUser }"
      :style="logoStyle"
      class="logo-container"
    >
      <img alt="MaxQuest" class="mq-logo" src="/images/logos/maxquest.svg" @click="goToLobby" />
    </div>

    <side-bar v-if="!isRouteIncluded && !isLandingPage && !isFooterLoggedOutPage" />
    <app-bar v-if="!isRouteIncluded" @mounted="appBarMounted = true" />
    <base-view />

    <confirm-dialog />
    <alert-dialog />
    <message-dialog />
    <prompt-dialog />
    <alert-snack-bar />

    <signup-dialog v-if="showSignupDialog" v-model="showSignupDialog" />
    <login-dialog v-if="showLoginDialog" v-model="showLoginDialog" />
    <reset-password-dialog v-if="showResetPasswordDialog" v-model="showResetPasswordDialog" />

    <event-day v-if="isLoggedInUser" />
    <dialog-game v-if="isLoggedInUser" />
    <dialog-add-friends v-if="isLoggedInUser" />
    <PromoPackPopup v-if="isLoggedInUser" />
    <CashierPopup v-if="isLoggedInUser" />
    <RoomBuyInPopup v-if="isLoggedInUser" />

    <VideoPopup v-if="!isLoggedInUser" />
  </v-app>
</template>

<style lang="scss">
  @import '@/scss/mixins.scss';

  .v-main .main-container {
    background: #1c0630;
  }

  .v-application .v-application--wrap {
    background: #250f3b;
  }

  .logo-container {
    position: fixed;
    top: 0;
    z-index: 10;
    transition: all 0.3s linear;

    &--logged-in .mq-logo {
      @media screen and (max-width: 599px) {
        display: none;
      }
    }

    .mq-logo {
      max-width: 120px;
      cursor: pointer;

      @media screen and (min-width: 380px) {
        max-width: 144px;
      }

      @media screen and (min-width: 510px) {
        max-width: 156px;
      }

      @media screen and (min-width: 1280px) {
        max-width: 200px;
      }
    }
  }

  .sidebar-overlay {
    width: 100%;
    height: 100%;
    position: fixed;
    inset: 0;
    z-index: 6;
    opacity: 0;
    visibility: hidden;
    background-color: #000;
    transition: opacity 0.2s ease;

    &--open {
      visibility: visible;
      opacity: 0.7;
    }
  }
</style>
